/**
 * Created by Mike Naughton on 3/8/18.
 * A read-only collection which provides access to an arbitrary set of features
 */

import Obscura from "backbone.obscura";
import extend from "backbone-extend-standalone";
import _ from "underscore";
import Backbone from "backbone";
import session from "app/Session";
import Enums from "app/enums";
import Field from "core/models/Field";
import GeoUtils from "app/utils/GeoUtils";
import SymbolFactory from "app/models/map/SymbolFactory";
import MapQuery from "app/models/map/MapQuery";
import async from "async";
import LayerContainerMixin from "./base/LayerContainer.mixin";
import QueryModel from "app/models/map/query/QueryModel";
import mapContext from "app/views/map/MapContext";

Obscura.extend = extend;

function buildMeasurementField(name, label, type, getMeasure) {
  var Units = window.require("esri/units");

  return {
    field: {
      id: name,
      name: name,
      label: label,
      type: Enums.FormQuestionType.Numeric,
      editable: false,
      filterable: true,
      sortable: true,
    },
    options: {
      getter: function (model) {
        var layer,
          geometry,
          val = NaN;

        try {
          layer = model.originalLayer || model.getFeature().originalLayer;
          geometry = layer.toGeometry(model).generateEsriGeometry();
        } catch (err) {
          console.error(err);
          _errs && _errs.push(err);
        }

        if (geometry && geometry.type == type) {
          val = GeoUtils.measure(geometry, Units.SQUARE_FEET, Units.FEET);
          val = getMeasure(val);
          if (val && _.isNumber(val.area)) {
            val = val.area.toFixed(2);
          }
        }

        return val;
      },
    },
  };
}

function normalizeSuperset(superset) {
  var normalized = superset;
  if (_.isArray(superset)) {
    // process superset as if it has heterogeneous data
    normalized = new Backbone.Collection(
      _.map(superset, function (model) {
        return model.originalLayer.toFeature(model);
      })
    );
  }

  // now we should have a Backbone.Collection to use.
  return normalized;
}

var FeatureResults = Obscura.extend(
  _.extend(
    {
      constructor: function (superset, options) {
        Obscura.call(this, normalizeSuperset(superset), options);

        this._visible = true;
      },

      /**
       * Converts the feature models in this collection to an array of data models
       * @return {Array} The resulting data models.
       */
      toData: function () {
        return this.map(function (model) {
          var result = model.originalLayer.toData(model);

          if (result) {
            result.originalLayer = model.originalLayer;
          }

          return result;
        });
      },

      /**
       * Converts the feature models in this collection to an array of geometry models
       * @return {Array} The resulting geometry models.
       */
      toGeometry: function () {
        return this.map(function (model) {
          var result = model.originalLayer.toGeometry(model);

          if (result) {
            result.originalLayer = model.originalLayer;
          }

          return result;
        });
      },

      /**
       * Gets the list of fields that are available for the data models of features within this collection. Only fields common to all models are returned.
       * @return {Array} Array of Field instances representing the available fields.
       */
      getSharedFields: function () {
        return _.map(this.getFields(true), function (field) {
          return field.decorate({
            editable: function (model) {
              var result = false;

              if (
                model &&
                model.originalLayer &&
                model.originalLayer.featureLayer
              ) {
                result = model.originalLayer.featureLayer.allows(
                  session,
                  "modify"
                );
              }

              return result;
            },
          });
        });
      },

      /**
       * Gets the service common to all of the models in this collection.
       * @return {MapServiceBase}         The service common to all given models, if any.
       */
      getSharedService: function () {
        var service = null;
        _.each(this.models, function (model) {
          var layer = model.originalLayer;

          if (layer && layer.featureLayer) {
            if (service) {
              if (service.service.id != layer.serviceLayer.service.id) {
                service = false;
              }
            } else {
              if (service !== false) {
                service = layer.serviceLayer;
              }
            }
          }
        });

        return service;
      },

      /**
       * Gets the list of fields that are available for the models in this collection, including some measurement fields.
       * @return {Array} Array of Field instances representing the available fields.
       */
      getExportFields: function () {
        var result = this.getSharedFields();
        var service = this.getSharedService();

        if (service) {
          result.unshift(service.getLayerField());
          result.unshift(service.getServiceField());
        }

        var geometryTypesContained = _.unique(
          _.compact(
            this.map(function (item) {
              return item.getGeometry().getGeometryType();
            })
          )
        );

        var potentialFieldsObject = {
          polygon: buildMeasurementField(
            "Area",
            "Area (Sq. Ft)",
            "polygon",
            function (val) {
              if (val && _.isNumber(val.area)) {
                val = val.area.toFixed(2);
              }

              return val;
            }
          ),
          polyline: buildMeasurementField(
            "Length",
            "Length (Ft.)",
            "polyline",
            function (val) {
              if (val && _.isNumber(val.length)) {
                val = val.length.toFixed(2);
              }

              return val;
            }
          ),
          point: [
            {
              field: {
                id: "Latitude",
                name: "Latitude",
                label: "Lat",
                type: Enums.FormQuestionType.Numeric,
                editable: false,
                filterable: true,
                sortable: true,
              },
              options: {
                getter: function (model) {
                  var layer = model.originalLayer;
                  var geometry = layer.toGeometry(model);

                  var centerLngLat = geometry.getCenter(4326); //Gets center in 4326, which is standard Lat/Lng

                  if (centerLngLat && _.isNumber(centerLngLat.y)) {
                    return centerLngLat.y.toFixed(6);
                  }
                },
              },
            },
            {
              field: {
                id: "Longitude",
                name: "Longitude",
                label: "Lng",
                type: Enums.FormQuestionType.Numeric,
                editable: false,
                filterable: true,
                sortable: true,
              },
              options: {
                getter: function (model) {
                  var layer = model.originalLayer;
                  var geometry = layer.toGeometry(model);

                  var centerLngLat = geometry.getCenter(4326); //Gets center in 4326, which is standard Lat/Lng

                  if (centerLngLat && _.isNumber(centerLngLat.x)) {
                    return centerLngLat.x.toFixed(6);
                  }
                },
              },
            },
          ],
        };

        // Iterates over geometry types contained in the layer. For every geometry type, checks if it has fields that need to be added to tabular and excel outputs.
        // e.g If geometryTypesContained = ['point', 'polygon'], will add Fields to excel export if they exist in potentialFieldObject.
        _.each(geometryTypesContained, function (fieldName) {
          var fieldsToAdd = potentialFieldsObject[fieldName];

          if (fieldsToAdd) {
            if (!_.isArray(fieldsToAdd)) {
              fieldsToAdd = [fieldsToAdd];
            }
            _.each(fieldsToAdd, function (indvField) {
              result.push(new Field(indvField.field, indvField.options));
            });
          }
        });

        return result;
      },

      /**
       * Gets a value indicating whether or not any of the features in this collection are from external services
       * @return {Boolean} True if there are external features in the mix.
       */
      anyExternal: function () {
        return this.any(function (model) {
          return model.isExternal();
        });
      },

      /**
       * Gets a value indicating whether or not any of the features in this collection are from internal services
       * @return {Boolean} True if there are internal features in the mix.
       */
      anyInternal: function () {
        return this.any(function (model) {
          return !model.isExternal();
        });
      },

      /**
       * Executes a spatial query on the models in this collection.
       * @param {Array} layers The list of layers on which to search for data.
       * @param  {Function}   prepareQuery Function which sets up the query and executes it. Passed an `esri.Geometry` indicating the search area and a MapQuery. Expected to return a promise which resolves with the prepared query or rejects with an error.
       * @param {Object} [options] An options hash for the queries.
       * @param {SymbolFactory} [options.primaryHighlight] A highlighter to use for results of the query. Defaults to `SymbolFactory.highlight()`.
       * @param {SymbolFactory} [options.secondaryHighlight] A highlighter to use for the query source geometries. Defaults to a "blue" SymbolFactory.
       * @param  {Function} cb       Callback which is invoked after all queries have executed or an error occurs. Arguments are error and array of query results.
       */
      executeQuery: function (layers, prepareQuery, options, cb) {
        var models = this.models;
        var opts = {
          primaryHighlight: SymbolFactory.highlight(),
          secondaryHighlight: new SymbolFactory({
            color: "blue",
            fillColor: "blue",
            fillAlpha: 0.3,
          }),
        };

        if (_.isFunction(options)) {
          cb = options;
          options = opts;
        } else {
          options = _.defaults({}, options, opts);
        }

        if (
          _.find(layers, function (layer) {
            return !layer.serviceLayer.service.get("external");
          })
        ) {
          //return cb('Running spatial queries on internal services is not yet supported.');
        }

        async.map(
          models,
          function (model, cb) {
            var layer = model.originalLayer;
            var graphic = layer.toGraphic(model);

            async.waterfall(
              [
                function findGeometry(cb) {
                  if (model.isExternal()) {
                    var boundaryQuery = new MapQuery(layer);

                    boundaryQuery.ids(layer.getId(model));
                    boundaryQuery
                      .execute()
                      .done(function (results) {
                        var gr = _.first(results);

                        if (gr) {
                          cb(null, gr.geometry);
                        } else {
                          cb("No boundary found.");
                        }
                      })
                      .fail(function (err) {
                        cb(err);
                      });
                  } else {
                    cb(null, graphic.geometry);
                  }
                },
                function prepare(geometry, cb) {
                  var queryModel = new QueryModel();
                  var internalServiceLayers = queryModel.filterSelectedLayers(
                    true,
                    layers
                  );
                  var externalServiceLayers = queryModel.filterSelectedLayers(
                    false,
                    layers
                  );

                  if (internalServiceLayers.length == 0) queryModel = null;

                  if (externalServiceLayers.length > 0) {
                    var query = new MapQuery(externalServiceLayers);
                    prepareQuery(geometry, query)
                      .done(function (query) {
                        cb(null, geometry, query, queryModel);
                      })
                      .fail(function (err) {
                        cb(err);
                      });
                  } else cb(null, geometry, null, queryModel);
                },
              ],
              function process(err, geometry, query, queryModel) {
                var models = [];
                //Internal Service's Layers
                if (queryModel != null) {
                  var internalServiceLayers = queryModel.filterSelectedLayers(
                    true,
                    layers
                  );
                  models = queryModel.runSpatialQuery(
                    geometry,
                    internalServiceLayers
                  );
                  mapContext.getOperation("highlight-geometry").action(models);
                }

                //External Service's Layers
                if (query != null) {
                  query
                    .highlight(options.primaryHighlight)
                    .done(function (results) {
                      layer.highlight(options.secondaryHighlight, model);
                      models = models.concat(results);
                      cb(null, models);
                    })
                    .fail(function (err) {
                      cb(err);
                    });
                } else cb(null, models);
              }
            );
          },
          function (err, resultSets) {
            if (err) {
              return cb(err);
            }

            cb(null, _.flatten(resultSets));
          }
        );
      },

      getExtent: function () {
        var geometries = this.map(function (model) {
          return model.originalLayer.toGraphic(model).geometry;
        });

        return GeoUtils.unionExtents(geometries);
      },

      show: function () {
        this.each(function (model) {
          model.originalLayer.toGraphic(model).show();
        });
      },

      hide: function () {
        this.each(function (model) {
          model.originalLayer.toGraphic(model).hide();
        });
      },

      setSelectable: function (selectable) {},

      isSelectable: function () {
        return true;
      },

      /**
       * Gets a value indicating whether or not all of the features in this set are visible.
       * @return {Boolean} True if visible, otherwise false.
       * @note This function is O(n).
       */
      isVisible: function () {
        return this.every(function (model) {
          return model.originalLayer.toGraphic(model).visible;
        });
      },

      getLayers: function () {
        return _.compact(_.unique(_.pluck(this.models, "originalLayer")));
      },

      getWrappedModel: function () {
        return null;
      },
    },
    LayerContainerMixin
  )
);

export default FeatureResults;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/FeatureResults.js
// module id = 137
// module chunks = 1